import { useCallback, useEffect, useState } from 'react'
import { CalendarDays } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { NativeSelect } from '@/components/ui/native-select'
import { CalendarDaysTable } from '@/components/deadlines/CalendarDaysTable'
import { DeadlineCalculatorCard } from '@/components/deadlines/DeadlineCalculatorCard'
import { SuspensionsPanel } from '@/components/deadlines/SuspensionsPanel'
import { useAuth } from '@/contexts/AuthContext'
import { isSocio } from '@/lib/roles'
import * as toast from '@/lib/toast'
import { deadlineService, type CalendarDay, type Suspension, type SuspensionInput, type Tribunal, type YearCoverage } from '@/services/deadline-service'

const YEARS = [2026, 2027]
const apiMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? fallback

/** Configurações › Calendário forense: dias sem prazo com fonte, suspensões (sócio) e simulador. */
export function ForensicCalendarPage() {
  const { user } = useAuth()
  const canEdit = isSocio(user?.role)
  const [tribunais, setTribunais] = useState<Tribunal[]>([])
  const [cobertura, setCobertura] = useState<YearCoverage[]>([])
  const [tribunal, setTribunal] = useState('TJBA')
  const [ano, setAno] = useState(2026)
  const [inativos, setInativos] = useState(false)
  const [days, setDays] = useState<CalendarDay[]>([])
  const [suspensions, setSuspensions] = useState<Suspension[]>([])
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    deadlineService.tribunais()
      .then((data) => { setTribunais(data.tribunais); setCobertura(data.cobertura) })
      .catch((err) => setError(apiMessage(err, 'Não foi possível carregar os tribunais')))
  }, [])

  const load = useCallback(async () => {
    try {
      const data = await deadlineService.calendar({ tribunal, ano, inativos })
      setDays(data.dias)
      setSuspensions(data.suspensoes)
      setError(null)
    } catch (err) {
      setError(apiMessage(err, 'Não foi possível carregar o calendário'))
    }
  }, [tribunal, ano, inativos])

  useEffect(() => { void load() }, [load])

  const toggle = async (day: CalendarDay, patch: { ativo?: boolean; aConferir?: boolean }) => {
    try {
      const updated = await deadlineService.updateDay(day.id, patch)
      setDays((list) => list.map((d) => (d.id === updated.id ? updated : d)))
    } catch (err) {
      toast.error('Não foi possível alterar', apiMessage(err, 'Erro'))
    }
  }

  const create = async (input: SuspensionInput) => {
    try {
      await deadlineService.createSuspension(input)
      toast.success('Suspensão cadastrada', 'Os próximos cálculos do tribunal já a consideram.')
      await load()
      return true
    } catch (err) {
      toast.error('Não foi possível cadastrar', apiMessage(err, 'Erro'))
      return false
    }
  }

  const cancel = async (s: Suspension) => {
    try {
      await deadlineService.cancelSuspension(s.id)
      await load()
    } catch (err) {
      toast.error('Não foi possível cancelar', apiMessage(err, 'Erro'))
    }
  }

  const coverage = cobertura.find((c) => c.tribunal === tribunal && c.ano === ano)
  const aConferir = days.filter((d) => d.aConferir && d.ativo).length

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      <div className="flex items-center gap-2">
        <CalendarDays className="size-6 text-muted-foreground" />
        <h1 className="text-2xl font-semibold">Calendário forense</h1>
      </div>
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Label htmlFor="cal-tribunal">Tribunal</Label>
          <NativeSelect id="cal-tribunal" value={tribunal} onChange={(e) => setTribunal(e.target.value)}>
            {tribunais.map((t) => <option key={t.sigla} value={t.sigla}>{t.sigla} — {t.ufs.join(', ')}</option>)}
            {!tribunais.length && <option value={tribunal}>{tribunal}</option>}
          </NativeSelect>
        </div>
        <div className="w-28">
          <Label htmlFor="cal-ano">Ano</Label>
          <NativeSelect id="cal-ano" value={ano} onChange={(e) => setAno(Number(e.target.value))}>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </NativeSelect>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={inativos} onChange={(e) => setInativos(e.target.checked)} /> Mostrar desativados
        </label>
      </div>
      {coverage && (
        <p className="text-sm text-muted-foreground">
          Calendário {coverage.ano} do {coverage.tribunal}: <strong>{coverage.situacao === 'COMPLETO' ? 'conferido' : 'parcial'}</strong> — {coverage.observacao}
          {aConferir > 0 && ` ${aConferir} dia(s) "a conferir".`}
        </p>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Simular prazo</CardTitle>
          <CardDescription>Mesmo cálculo usado nas publicações (disponibilização → publicação → início → vencimento).</CardDescription>
        </CardHeader>
        <CardContent><DeadlineCalculatorCard tribunal={tribunal} /></CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Suspensões e feriados do tribunal</CardTitle>
          <CardDescription>Cadastro do sócio; vale para todo o tribunal (ou só a UF escolhida). Feriado municipal não entra no cálculo: na dúvida, vale o prazo mais seguro.</CardDescription>
        </CardHeader>
        <CardContent>
          <SuspensionsPanel tribunal={tribunal} suspensions={suspensions} canEdit={canEdit} onCreate={create} onCancel={cancel} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Dias sem prazo</CardTitle>
          <CardDescription>Feriados, pontos facultativos, suspensões e recessos que valem para o tribunal, com a fonte de cada um.</CardDescription>
        </CardHeader>
        <CardContent><CalendarDaysTable days={days} canEdit={canEdit} onToggle={toggle} /></CardContent>
      </Card>
    </div>
  )
}
